import type {
  AmendLineInput,
  FulfillmentOrder,
  FulfillmentOrderCreateInput,
  FulfillmentOrderPatch,
  FulfillmentState,
  FulfillmentTemplate,
  FulfillmentTemplateInput,
  PickInput,
  PickResult,
  StockBalance,
  StockItem,
  WarehouseLocation,
  HandoverMethod,
} from '../domain/fulfillment';
import { fulfillmentApi as liveFulfillmentApi } from './live-fulfillment-api';
import { fulfillmentApi as mockFulfillmentApi } from './mock-fulfillment-api';

export interface FulfillmentOrderFilters {
  states?: FulfillmentState[] | undefined;
  siteId?: string | undefined;
  customerId?: string | undefined;
  fieldWorkRequestId?: string | undefined;
}

/**
 * Pick-pack, backed by leatherback's FulfillmentService, plus the stock
 * reads it needs from StockService (tidaltech/koi#8083, #8084). Rejections
 * with a stable backend reason throw FulfillmentError.
 */
export interface FulfillmentApi {
  listOrders(filters?: FulfillmentOrderFilters): Promise<FulfillmentOrder[]>;
  getOrder(id: string): Promise<FulfillmentOrder>;
  createOrder(input: FulfillmentOrderCreateInput): Promise<FulfillmentOrder>;
  updateOrder(patch: FulfillmentOrderPatch): Promise<FulfillmentOrder>;
  /**
   * Reserves the order's stock and opens it for picking. A shortfall fails
   * with INSUFFICIENT_STOCK unless allowShortfall lets short lines wait.
   */
  releaseOrder(id: string, version: number, allowShortfall?: boolean): Promise<FulfillmentOrder>;
  pick(input: PickInput): Promise<PickResult>;
  removePick(orderId: string, pickId: string, reason: string): Promise<FulfillmentOrder>;
  /** Reduce, replace or cancel a line of a released order. */
  amendLine(input: AmendLineInput): Promise<FulfillmentOrder>;
  cancelOrder(id: string, version: number, reason: string): Promise<FulfillmentOrder>;
  completeOrder(
    id: string,
    version: number,
    recipient: string,
    shipmentId?: string,
    method?: HandoverMethod,
  ): Promise<FulfillmentOrder>;

  listTemplates(includeInactive?: boolean): Promise<FulfillmentTemplate[]>;
  createTemplate(input: FulfillmentTemplateInput): Promise<FulfillmentTemplate>;
  updateTemplate(
    id: string,
    version: number,
    input: FulfillmentTemplateInput,
  ): Promise<FulfillmentTemplate>;

  listStockItems(): Promise<StockItem[]>;
  listWarehouseLocations(siteId?: string): Promise<WarehouseLocation[]>;
  listStockBalances(stockItemId?: string): Promise<StockBalance[]>;
}

export const fulfillmentApi: FulfillmentApi =
  import.meta.env.VITE_API_MODE === 'live' ? liveFulfillmentApi : mockFulfillmentApi;
